/**
 * Marketing Evolve — matriz comercial (planilha + e-mail/automação).
 * Total: 69h10/mês (51h37 operação + 17h33 CRM/e-mail) · Valor mínimo: R$ 5.499.
 */

export const MARKETING_EVOLVE_NAME = 'Marketing Evolve'
export const MARKETING_EVOLVE_BASE_VALUE = 5499
/** 51h37 (matriz principal) + 17h33 (automação + disparos). */
export const MARKETING_EVOLVE_TOTAL_HOURS = 69.17
export const MARKETING_EVOLVE_HOURLY_RATE =
  Math.round((MARKETING_EVOLVE_BASE_VALUE / MARKETING_EVOLVE_TOTAL_HOURS) * 100) / 100

export const MARKETING_EVOLVE_AUTOMATION_QTY = 4
export const MARKETING_EVOLVE_EMAIL_QTY = 5

export const MARKETING_EVOLVE_CATEGORIES = {
  trafego: 'Gestão de Tráfego e Performance',
  acompanhamento: 'Acompanhamento Estratégico',
  conteudoSeo: 'Conteúdo, SEO e Suporte',
  crm: 'CRM & Automação de E-mail',
} as const

/** Layout 2 colunas no topo + CRM à direita no meio + acompanhamento embaixo à esquerda. */
export const MARKETING_EVOLVE_CATEGORY_COORDS = {
  [MARKETING_EVOLVE_CATEGORIES.trafego]: { x: 38, y: 13.9 },
  [MARKETING_EVOLVE_CATEGORIES.conteudoSeo]: { x: 68, y: 13.9 },
  [MARKETING_EVOLVE_CATEGORIES.crm]: { x: 68, y: 48 },
  [MARKETING_EVOLVE_CATEGORIES.acompanhamento]: { x: 38, y: 63.4 },
} as const

/** Slide completo — blocos fixos + categorias (export e preview). */
export const MARKETING_EVOLVE_PROPOSAL_LAYOUT = {
  leftBlockWidth: 35,
  titleY: 7.1,
  projectTypeY: 16.4,
  descriptionY: 27.3,
  bonusY: 48,
  priceY: 82.8,
  paymentY: 90,
  loyaltyY: 74.8,
  planTitleY: 2.7,
  serviceBlockX: 38,
  serviceBlockGap: 68,
  serviceBlockStartY: 10,
  categoryCoords: { ...MARKETING_EVOLVE_CATEGORY_COORDS },
} as const

/** Converte horas da planilha (h:mm) para decimal. */
function hm(hours: number, minutes: number): number {
  return Math.round((hours + minutes / 60) * 100) / 100
}

export const marketingEvolveDeliverables = [
  {
    name: 'Configuração e manutenção de plataformas de tráfego',
    category: MARKETING_EVOLVE_CATEGORIES.trafego,
    quantity: 'Sob demanda',
    hours: hm(0, 49),
  },
  {
    name: 'Criação e Gerenciamento de Campanhas recorrentes em plataformas de tráfego (Meta/Google/TikTok/Pinterest)',
    category: MARKETING_EVOLVE_CATEGORIES.trafego,
    quantity: '3',
    hours: hm(2, 27),
  },
  {
    name: 'Criação e Gerenciamento de campanhas sazonais em plataformas de tráfego (Meta/Google/TikTok/Pinterest)',
    category: MARKETING_EVOLVE_CATEGORIES.trafego,
    quantity: '2',
    hours: hm(1, 38),
  },
  {
    name: 'Card estático para anúncios (até duas variações por campanha)',
    category: MARKETING_EVOLVE_CATEGORIES.trafego,
    quantity: '5',
    hours: hm(9, 0),
  },
  {
    name: 'Carrossel para anúncio',
    category: MARKETING_EVOLVE_CATEGORIES.trafego,
    quantity: '2',
    hours: hm(3, 36),
  },
  {
    name: 'Criação de Vídeo Motion para anúncio',
    category: MARKETING_EVOLVE_CATEGORIES.trafego,
    quantity: '1',
    hours: hm(2, 21),
  },
  {
    name: 'Reunião de alinhamento e relatório mensal',
    category: MARKETING_EVOLVE_CATEGORIES.acompanhamento,
    quantity: '1',
    hours: hm(2, 21),
  },
  {
    name: 'Reunião de acompanhamento Semanal',
    category: MARKETING_EVOLVE_CATEGORIES.acompanhamento,
    quantity: 'Semanal',
    hours: hm(1, 55),
  },
  {
    name: 'Análise, atendimento e consultoria',
    category: MARKETING_EVOLVE_CATEGORIES.acompanhamento,
    quantity: 'Diário',
    hours: hm(3, 44),
  },
  {
    name: 'Dashboard de Performance',
    category: MARKETING_EVOLVE_CATEGORIES.acompanhamento,
    quantity: 'Indefinido',
    hours: hm(0, 31),
  },
  {
    name: 'Criação de artes digitais extras (Banners para site, Card, Storie, entre outros)',
    category: MARKETING_EVOLVE_CATEGORIES.conteudoSeo,
    quantity: '8',
    hours: hm(18, 48),
  },
  {
    name: 'Gestão de site ou redes sociais: atualização de bio, banners/capas, vitrines, promos, cupons',
    category: MARKETING_EVOLVE_CATEGORIES.conteudoSeo,
    quantity: 'Sob demanda',
    hours: hm(2, 8),
  },
  {
    name: 'SEO - Análise, execuções técnicas e acompanhamento',
    category: MARKETING_EVOLVE_CATEGORIES.conteudoSeo,
    quantity: 'Mensal',
    hours: hm(2, 19),
  },
  {
    name: 'Gerenciamento de automações',
    category: MARKETING_EVOLVE_CATEGORIES.crm,
    quantity: String(MARKETING_EVOLVE_AUTOMATION_QTY),
    hours: hm(7, 48),
  },
  {
    name: 'Disparos E-mail marketing',
    category: MARKETING_EVOLVE_CATEGORIES.crm,
    quantity: String(MARKETING_EVOLVE_EMAIL_QTY),
    hours: hm(9, 45),
  },
] as const

function buildServicesFromDeliverables(
  deliverables: ReadonlyArray<{
    name: string
    category: string
    quantity: string
    hours?: number
  }>,
) {
  const byCategory = new Map<
    string,
    Array<{ name: string; isIncluded: boolean; quantity: string; hours: number }>
  >()
  for (const d of deliverables) {
    const list = byCategory.get(d.category) ?? []
    list.push({
      name: d.name,
      isIncluded: true,
      quantity: d.quantity,
      hours: Number(d.hours ?? 0),
    })
    byCategory.set(d.category, list)
  }
  return Array.from(byCategory.entries()).map(([category, services]) => ({ category, services }))
}

export type MarketingEvolveConfigOptions = {
  baseValue?: number
  hourlyRate?: number
  showBonus?: boolean
  bonus?: string
}

export function buildMarketingEvolveConfig(options: MarketingEvolveConfigOptions = {}) {
  const deliverables = marketingEvolveDeliverables.map((d) => ({ ...d }))
  const totalHours = Math.round(deliverables.reduce((acc, d) => acc + d.hours, 0) * 100) / 100
  const baseValue = options.baseValue ?? MARKETING_EVOLVE_BASE_VALUE
  const hourlyRate =
    options.hourlyRate ?? Math.round((baseValue / totalHours) * 100) / 100

  return {
    projectType: 'Plano de Marketing',
    showBonus: options.showBonus ?? true,
    bonus: options.bonus ?? 'Setup prioritário de automações',
    duration: 12,
    hourlyRate,
    totalHours,
    suggestedValue: baseValue,
    maxHoursMonthly: 70,
    pricing: {
      entry: baseValue,
      total: baseValue * 12,
      installments: 12,
      installmentValue: baseValue,
      recorrente: true,
      duracao: 12,
    },
    services: buildServicesFromDeliverables(deliverables),
    deliverables,
    proposalLayout: { ...MARKETING_EVOLVE_PROPOSAL_LAYOUT },
  }
}

export const marketingEvolvePlanSeed = {
  name: MARKETING_EVOLVE_NAME,
  description:
    'Estrutura completa para e-commerce: mídia paga multicanal, criativos, SEO, automação e e-mail marketing (4 automações + 5 disparos/mês), dados e acompanhamento intensivo.',
  baseValue: MARKETING_EVOLVE_BASE_VALUE,
  category: 'Marketing',
  type: 'RECORRENTE' as const,
  isActive: true,
  config: buildMarketingEvolveConfig(),
}
